/* iter55z-audit round 9 — Editor Add-on A/B Price Test panel.
 *
 * Backend: GET /admin/ab-test/editor-addon (+ PUT), /admin/ab-test/editor-addon/results.
 *
 * The admin toggles the test on/off and edits the 3 price variants
 * ($49 / $59 / $69 defaults). Results row shows impressions,
 * conversions, conversion rate, and MRR per impression per variant,
 * plus a "Winner" badge once each qualified variant has ≥20
 * impressions.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FlaskConical, Save, Plus, Trash2, Trophy } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function EditorAddonAbTestPanel() {
  const [cfg, setCfg]         = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [c, r] = await Promise.all([
        http.get('/admin/ab-test/editor-addon'),
        http.get('/admin/ab-test/editor-addon/results'),
      ]);
      setCfg(c.data);
      setResults(r.data);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await http.put('/admin/ab-test/editor-addon', {
        enabled: !!cfg.enabled,
        variants: (cfg.variants || []).map((v) => ({
          id: (v.id || '').trim(),
          price_usd: parseFloat(v.price_usd) || 0,
          weight: parseInt(v.weight, 10) || 0,
        })),
      });
      toast.success('A/B config saved.');
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  const updateVariant = (idx, patch) => {
    setCfg((c) => ({
      ...c,
      variants: c.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)),
    }));
  };
  const addVariant = () => {
    setCfg((c) => ({
      ...c,
      variants: [...(c.variants || []), { id: `v${((c.variants || []).length + 1) * 10}`, price_usd: 59, weight: 1 }],
    }));
  };
  const removeVariant = (idx) => {
    setCfg((c) => ({ ...c, variants: c.variants.filter((_, i) => i !== idx) }));
  };

  const resultsByVariant = (vid) => (results?.variants || []).find((r) => r.variant_id === vid);

  return (
    <section data-testid="admin-editor-addon-ab-panel" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <FlaskConical className="h-3.5 w-3.5" /> Editor Add-on price A/B test
        </div>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {!cfg ? (
        <div className="flex h-24 items-center justify-center text-[12px] text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <label className="mb-3 flex cursor-pointer items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={!!cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="ab-test-enabled"
              className="accent-primary"
            />
            <span className="font-semibold text-foreground">
              {cfg.enabled ? 'A/B test ACTIVE' : 'A/B test disabled — Premier shows control price'}
            </span>
          </label>

          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="ab-test-variants-table">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="py-2 pr-3">Variant ID</th>
                  <th className="py-2 pr-3 text-right">Price</th>
                  <th className="py-2 pr-3 text-right">Weight</th>
                  <th className="py-2 pr-3 text-right">Impressions</th>
                  <th className="py-2 pr-3 text-right">Conversions</th>
                  <th className="py-2 pr-3 text-right">Rate</th>
                  <th className="py-2 pr-3 text-right">MRR / imp</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {(cfg.variants || []).map((v, i) => {
                  const r = resultsByVariant(v.id);
                  return (
                    <tr key={i} className="border-b border-border/60" data-testid={`ab-test-row-${v.id}`}>
                      <td className="py-2 pr-3">
                        <input
                          value={v.id}
                          onChange={(e) => updateVariant(i, { id: e.target.value })}
                          data-testid={`ab-test-row-${i}-id`}
                          className="h-6 w-20 rounded-sm border border-border bg-background px-2 font-mono text-[11.5px] focus:border-primary"
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <input
                          type="number" step="1" min="0" max="999"
                          value={v.price_usd}
                          onChange={(e) => updateVariant(i, { price_usd: e.target.value })}
                          data-testid={`ab-test-row-${i}-price`}
                          className="h-6 w-20 rounded-sm border border-border bg-background px-2 text-right font-mono text-[11.5px] focus:border-primary"
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <input
                          type="number" step="1" min="0" max="100"
                          value={v.weight}
                          onChange={(e) => updateVariant(i, { weight: e.target.value })}
                          data-testid={`ab-test-row-${i}-weight`}
                          className="h-6 w-16 rounded-sm border border-border bg-background px-2 text-right font-mono text-[11.5px] focus:border-primary"
                        />
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{r?.impressions ?? 0}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r?.conversions ?? 0}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r ? `${r.conversion_rate_pct.toFixed(1)}%` : '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r ? `$${r.mrr_per_impression_usd.toFixed(2)}` : '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        {r?.is_winner && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10.5px] font-bold text-amber-800 dark:text-amber-300"
                                data-testid={`ab-test-winner-${v.id}`}>
                            <Trophy className="h-3 w-3" /> Winner
                          </span>
                        )}
                        <button
                          onClick={() => removeVariant(i)}
                          className="ml-1 rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-destructive"
                          data-testid={`ab-test-row-${i}-remove`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={addVariant}
              data-testid="ab-test-add-variant"
              className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-[11.5px] hover:bg-secondary/80"
            >
              <Plus className="h-3 w-3" /> Add variant
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="ab-test-save"
              className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save config
            </button>
            <span className="ml-auto text-[11px] text-muted-foreground">
              Qualification threshold: ≥{results?.qualification_threshold ?? 20} impressions.
              Users are bucketed deterministically by user_id — same variant every visit.
            </span>
          </div>
        </>
      )}
    </section>
  );
}
